
import { TitleHeading } from "@/components/helper/TitleHeading";
import SpotlightCard from "@/components/reactbits/SpotlightCard";
import { useFinePointerMotion } from "@/hooks/useFinePointerMotion";
import "../../styles/services.css";
import React from "react";
import { SiBnbchain } from "react-icons/si";
import { RiRobot3Fill } from "react-icons/ri";
import { BaseSection, BaseSectionProps } from "@/components/sections/BaseSection";
import { MdAutoAwesome } from "react-icons/md";
import { IoBriefcaseOutline } from "react-icons/io5";
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from 'react-router-dom';
import { getScrollBehavior } from "@/utils/scroll";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

/**
 * Highlights key technical terms and value propositions in service descriptions.
 * Returns an array of React elements with highlighted keywords.
 * Supports English, Japanese, and Traditional Chinese keywords.
 */
function highlightKeywords(text: string): React.ReactNode[] {
    // Define keywords to highlight - technical terms and value propositions
    // English keywords
    const englishKeywords = [
        'Claude Code', 'MCP servers', 'agent pipelines',
        'OpenZeppelin', 'ERC20', 'ERC721', 'ERC1155', 'ERC4626', 'ERC4337',
        'account abstraction', 'security audits', 'tokenomics',
        'LangChain', 'n8n', 'AI agents', 'RAG',
        'Next\\.js', 'React', 'NestJS', 'Go', 'Rust', 'Swift', 'Kotlin', 'Flutter',
        'quantitative trading', 'backtesting', 'automated trading',
        'hirable', 'founding engineer', 'CTO',
        'EVM', 'SVM', 'AptosVM', 'CEX', 'DEX',
        'upgradeable', 'Chainlink'
    ];

    // Japanese keywords
    const japaneseKeywords = [
        'Claude Code', 'MCP', 'AI エージェント', 'AI スキル',
        'OpenZeppelin', 'ERC20', 'ERC721', 'ERC1155', 'ERC4626', 'ERC4337',
        'アカウント抽象化', 'セキュリティ監査', 'トークノミクス',
        'LangChain', 'AutoGen', 'n8n', '自律型エージェント',
        'Next\\.js', 'React', 'NestJS', 'Go', 'Rust', 'Swift', 'Kotlin', 'Flutter',
        '定量的トレーディング', 'バックテスト', '自動取引',
        'hirable', 'founding engineer', 'CTO',
        'EVM', 'SVM', 'AptosVM', 'CEX', 'DEX',
        '自動コードレビュー'
    ];

    // Traditional Chinese keywords
    const chineseKeywords = [
        'Claude Code', 'MCP', 'AI Agent', 'AI 技能',
        'OpenZeppelin', 'ERC20', 'ERC721', 'ERC1155', 'ERC4626', 'ERC4337',
        '帳戶抽象化', '安全審計', '代幣經濟',
        'LangChain', 'AutoGen', 'n8n', '自主式 Agent',
        'Next\\.js', 'React', 'NestJS', 'Go', 'Rust', 'Swift', 'Kotlin', 'Flutter',
        '量化交易', '回測', '自動交易',
        '創辦工程師', '创始工程师', 'CTO', '競爭力', '竞争力',
        '打造產品', '打造产品', '解決問題', '解决问题',
        'EVM', 'SVM', 'AptosVM', 'CEX', 'DEX',
        '自動化程式碼審查'
    ];

    // Combine all keywords
    const allKeywords = [...englishKeywords, ...japaneseKeywords, ...chineseKeywords];

    // Create a regex pattern to match all keywords
    // Use word boundaries for English words, but not for CJK characters
    const escapedKeywords = allKeywords.map(kw => kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // Find and highlight all keyword matches
    while ((match = pattern.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }

        // Add highlighted keyword
        parts.push(
            <mark key={`${match.index}-${match[0]}`} className="keyword-highlight">
                {match[0]}
            </mark>
        );

        lastIndex = pattern.lastIndex;
    }

    // Add remaining text after the last match
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : [text];
}

type ServiceItem = {
    icon: React.JSX.Element,
    name: string,
    nameKey: string,
    descriptionKey: string,
    featured?: boolean,
    targetAudienceKeys: string[],
    linkTo?: string,
    linkLabelKey?: string,
}

const SPOTLIGHT_COLOR = "color-mix(in srgb, var(--color-accent) 18%, transparent)";

export function Services(props: BaseSectionProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { locale } = useParams();
    const prefersReducedMotion = usePrefersReducedMotion();
    const finePointerMotion = useFinePointerMotion();

    const serviceList: ServiceItem[] = [
        {
            icon: <MdAutoAwesome size={26} />,
            name: "Forward-Deployed AI Engineering",
            nameKey: "services.aiWorkflow.title",
            descriptionKey: "services.aiWorkflow.description",
            featured: true,
            targetAudienceKeys: [
                "services.aiWorkflow.audience1",
                "services.aiWorkflow.audience2",
                "services.aiWorkflow.audience3"
            ],
            linkTo: "#contact",
            linkLabelKey: "services.cta.discuss",
        },
        {
            icon: <RiRobot3Fill size={26} />,
            name: "AI Agents & Automation",
            nameKey: "services.aiAgent.title",
            descriptionKey: "services.aiAgent.description",
            featured: true,
            targetAudienceKeys: [
                "services.aiAgent.audience1",
                "services.aiAgent.audience2",
                "services.aiAgent.audience3"
            ],
            linkTo: "#contact",
            linkLabelKey: "services.cta.discuss",
        },
        {
            icon: <SiBnbchain size={26} />,
            name: "Smart Contracts & Web3",
            nameKey: "services.blockchain.title",
            descriptionKey: "services.blockchain.description",
            targetAudienceKeys: [
                "services.blockchain.audience1",
                "services.blockchain.audience2",
                "services.blockchain.audience3"
            ],
            linkTo: "#contact",
            linkLabelKey: "services.cta.discuss",
        },
        {
            icon: <IoBriefcaseOutline size={26} />,
            name: "Career Consultation",
            nameKey: "services.career.title",
            descriptionKey: "services.career.description",
            targetAudienceKeys: [
                "services.career.audience1",
                "services.career.audience2",
                "services.career.audience3"
            ],
            linkTo: "coaching",
            linkLabelKey: "coaching.learnMore",
        }
    ]

    return (
        <BaseSection {...props}>
            <div className="row" style={{ marginLeft: "unset " }}>
                <div className="col-12 col-md-10 col-xl-8">
                    <TitleHeading className={"title-heading mb-4"}>
                        {t('services.title')}
                    </TitleHeading>
                    <h2 className="section-subtitle">{t('services.subtitle')}</h2>
                </div>
            </div>
            <div className="">
                <ul className="services-list">
                    <AnimatePresence mode="sync">
                        {
                            serviceList.map((service: ServiceItem, index) => {
                                return (
                                    <motion.li
                                        className={`services-item ${service.featured ? 'is-featured' : ''}`}
                                        key={service.name}
                                        initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                                        animate={{
                                            opacity: props.section.viewHook.inView ? 1 : 0,
                                            y: props.section.viewHook.inView ? 0 : 24
                                        }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            duration: 0.8,
                                            ease: [0.16, 1, 0.3, 1],
                                            delay: index * 0.06
                                        }}
                                    >
                                        {finePointerMotion && (
                                            <SpotlightCard
                                                className="-z-10"
                                                color={SPOTLIGHT_COLOR}
                                            />
                                        )}
                                        <div className="services-row">
                                            <div className="services-lead">
                                                <div className="services-title">
                                                    <span className="services-icon" aria-hidden="true">
                                                        {service.icon}
                                                    </span>
                                                    <h6>{t(service.nameKey)}</h6>
                                                </div>
                                                {service.targetAudienceKeys && service.targetAudienceKeys.length > 0 && (
                                                    <div className="target-audience">
                                                        <div className="target-audience-inner">
                                                            <p className="target-audience-label">{t('services.perfectFor')}</p>
                                                            <ul className="target-audience-list">
                                                                {service.targetAudienceKeys.map((key, idx) => (
                                                                    <li key={idx}>{t(key)}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="services-description">
                                                <p className="text-theme-light">
                                                    {highlightKeywords(t(service.descriptionKey))}
                                                </p>
                                                {service.linkTo && (
                                                    <button
                                                        onClick={() => {
                                                            if (service.linkTo?.startsWith('#')) {
                                                                document.getElementById(service.linkTo.slice(1))?.scrollIntoView({ behavior: getScrollBehavior() });
                                                            } else {
                                                                navigate(`/${locale || 'en'}/${service.linkTo}`);
                                                            }
                                                        }}
                                                        className="services-cta-link"
                                                    >
                                                        {t(service.linkLabelKey || '')}
                                                        <span className="services-cta-arrow" aria-hidden="true">&rarr;</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </motion.li>
                                )
                            })
                        }
                    </AnimatePresence>
                </ul>
            </div>
        </BaseSection>
    );
}